import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { defer, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { JsonResponse } from '../shared/api/backend-config';
import { retryTransient } from '../shared/api/request-retry';
import { OrderIdempotencyKey } from './order-idempotency-key';
import { GuestOrderService } from './guest-order.service';
import type { RequestPricesResponse } from './reports.service';

/**
 * POST report/order, the one way every report is ordered.
 *
 * The call carries an `Idempotency-Key`, so the backend answers a repeat of it
 * with the first order instead of placing another. That is what makes the
 * automatic retry safe: it is the same request again, and it can only ever
 * come back as the one order.
 */
@Injectable({ providedIn: 'root' })
export class ReportOrderApiService {
  private readonly orderKey = new OrderIdempotencyKey();

  constructor(
    private http: HttpClient,
    private guestOrder: GuestOrderService
  ) {}

  /**
   * Cold: the key is taken on subscribe, so a resubscribe is a repeat.
   *
   * A guest heading to Stripe has its order's id kept here, the one place
   * every order passes, so My reports can show it when they come back.
   */
  public place(body: unknown): Observable<JsonResponse<RequestPricesResponse>> {
    return defer(() =>
      this.http.post<JsonResponse<RequestPricesResponse>>(
        `${environment.apiUrl}report/order`,
        body,
        { headers: this.orderKey.headersFor(body) }
      )
    ).pipe(
      retryTransient('report order'),
      tap((res) => {
        const requestId = res?.object?.requestId;
        if (requestId && res.object?.paymentUrl) {
          this.guestOrder.rememberCheckout(requestId);
        }
      })
    );
  }
}
